/**
 * Tag schema validation with Zod
 * @module schemas/tag
 */

import { z } from 'zod';
import { uuidSchema } from './common.schema.js';

/** Hex color pattern */
const hexColorPattern = /^#[0-9A-Fa-f]{6}$/;

/** Create tag request schema */
export const createTagRequestSchema = z.object({
  name: z
    .string()
    .min(1, 'Tag name is required')
    .max(100, 'Tag name must not exceed 100 characters'),
  color: z
    .string()
    .regex(hexColorPattern, 'Color must be a valid hex color (e.g., #6366f1)')
    .default('#6366f1'),
});

/** Tag response schema */
export const tagResponseSchema = z.object({
  id: uuidSchema,
  spaceId: uuidSchema,
  name: z.string(),
  color: z.string(),
  createdAt: z.string().datetime(),
  usageCount: z.number().int().nonnegative(),
});

/** Tag list response schema */
export const tagListResponseSchema = z.object({
  data: z.array(tagResponseSchema),
});

/** List tags query params */
export const listTagsQuerySchema = z.object({
  search: z.string().max(100).optional(),
});

// =============================================================================
// Type Inference
// =============================================================================

export type CreateTagRequestInput = z.infer<typeof createTagRequestSchema>;
export type TagResponseInput = z.infer<typeof tagResponseSchema>;
export type TagListResponseInput = z.infer<typeof tagListResponseSchema>;
export type ListTagsQueryInput = z.infer<typeof listTagsQuerySchema>;
